/**
 * The small, unopinionated primitives every feature reuses.
 *
 * One module rather than a file each: none of them has enough logic to justify
 * its own, and grouping them makes the whole vocabulary readable at once.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { ConnectionStatus } from '@/core/types/api.types'
import { colorFor, monogram } from '@/core/utils/logo.resolver'
import { formatError } from '@/core/utils/error.formatter'
import { Icon } from '@/shared/components/icons'

// --- logo -------------------------------------------------------------------

export function Logo({
  src,
  name,
  id,
  small,
}: {
  src?: string | null
  name: string
  id: string
  small?: boolean
}) {
  const [broken, setBroken] = useState(false)
  const cls = small ? 'logo logo-sm' : 'logo'

  if (!src || broken) {
    return (
      <div
        className={`${cls} logo-fallback`}
        style={{ background: colorFor(id) }}
        aria-hidden="true"
      >
        {monogram(name)}
      </div>
    )
  }
  return (
    <img
      className={cls}
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  )
}

// --- status -----------------------------------------------------------------

const STATUS_STYLE: Record<ConnectionStatus, { cls: string; text: string }> = {
  active: { cls: 'badge-ok', text: 'Healthy' },
  expired: { cls: 'badge-warn', text: 'Expired' },
  revoked: { cls: 'badge-danger', text: 'Revoked' },
  rate_limited: { cls: 'badge-warn', text: 'Rate limited' },
  error: { cls: 'badge-danger', text: 'Error' },
}

export function StatusBadge({ status }: { status: ConnectionStatus }) {
  const s = STATUS_STYLE[status] ?? { cls: 'badge-neutral', text: status }
  return (
    <span className={`badge ${s.cls}`}>
      <span className="dot" />
      {s.text}
    </span>
  )
}

export function MethodTag({ method }: { method: string }) {
  return (
    <span className="method" data-m={method.toUpperCase()}>
      {method.toUpperCase()}
    </span>
  )
}

// --- feedback ---------------------------------------------------------------

export function Spinner() {
  return <span className="spinner" role="status" aria-label="Loading" />
}

/**
 * Placeholder rows at the height the real rows will be.
 *
 * A table that loads behind a centred spinner jumps twice — once when the
 * spinner is replaced, once when the rows settle — and in between the page has
 * no shape at all. Skeleton rows keep the layout still and tell you roughly how
 * much is coming, which is the honest thing to show while you wait.
 */
export function SkeletonRows({
  rows = 6,
  columns = 4,
  label = 'Loading…',
}: {
  rows?: number
  columns?: number
  label?: string
}) {
  return (
    <div className="skel" role="status" aria-label={label} aria-busy="true">
      {Array.from({ length: rows }, (_, r) => (
        <div className="skel-row" key={r}>
          {Array.from({ length: columns }, (_, c) => (
            <span
              className="skel-cell"
              key={c}
              // Varying the widths stops the block reading as a loading graphic
              // and starts it reading as the table it is about to be.
              style={{ width: `${[38, 22, 16, 12, 18, 24][(r + c) % 6]}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty">
      <div className="row" style={{ justifyContent: 'center' }}>
        <Spinner />
        <span>{label}</span>
      </div>
    </div>
  )
}

export function EmptyState({
  title,
  children,
  action,
  icon = <Icon.inbox />,
}: {
  title: string
  children?: ReactNode
  action?: ReactNode
  /** A line icon from `Icon`; pass `null` for none. */
  icon?: ReactNode
}) {
  return (
    <div className="empty">
      {icon && (
        <span className="empty-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <h3>{title}</h3>
      {children && <p style={{ margin: 0, maxWidth: '44ch' }}>{children}</p>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  )
}

export function Callout({
  kind = 'neutral',
  title,
  children,
}: {
  kind?: 'neutral' | 'ok' | 'warn' | 'danger' | 'accent'
  title?: string
  children: ReactNode
}) {
  const cls = kind === 'neutral' ? '' : `callout-${kind}`
  return (
    <div className={`callout ${cls}`}>
      {title && <p className="callout-title">{title}</p>}
      {children}
    </div>
  )
}

/** Renders any thrown error consistently, including the actionable hint. */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const e = formatError(error)
  return (
    <Callout kind="danger" title={e.title}>
      <p>{e.message}</p>
      {e.hint && <p className="small">{e.hint}</p>}
      {e.details && <ProviderDetail error={error} />}
      <div className="row" style={{ marginTop: 8 }}>
        {onRetry && e.retryable && (
          <button className="btn btn-sm" onClick={onRetry}>
            Try again
          </button>
        )}
        <span className="faint small mono">{e.code}</span>
        {e.requestId && <span className="faint small mono">req {e.requestId.slice(0, 8)}</span>}
      </div>
    </Callout>
  )
}

function ProviderDetail({ error }: { error: unknown }) {
  const e = formatError(error)
  const body = e.details?.provider_body
  const [open, setOpen] = useState(false)
  if (body === undefined || body === null) return null
  return (
    <div style={{ marginTop: 8 }}>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide' : 'Show'} the provider's raw response
      </button>
      {open && <pre className="json" style={{ marginTop: 6 }}>{JSON.stringify(body, null, 2)}</pre>}
    </div>
  )
}

// --- modal ------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const dialog = dialogRef.current
    if (!dialog) return
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
    )).filter((element) => element.getClientRects().length > 0)
    ;(focusable()[0] ?? dialog).focus()
    const onKey = (event: KeyboardEvent) => {
      // Only the topmost dialog handles keys when dialogs are nested.
      const dialogs = document.querySelectorAll('.modal-backdrop')
      if (dialogs[dialogs.length - 1] !== dialog.parentElement) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        closeRef.current()
      }
      if (event.key === 'Tab') {
        const elements = focusable()
        const first = elements[0]
        const last = elements[elements.length - 1]
        if (!first) { event.preventDefault(); dialog.focus(); return }
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault(); last.focus()
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault(); first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      if (previous?.isConnected) previous.focus()
    }
  }, [])

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div ref={dialogRef} tabIndex={-1} className="modal" style={wide ? { maxWidth: 720 } : undefined}>
        <div className="modal-head">
          <h2 style={{ flex: 1 }}>{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  )
}

// --- misc -------------------------------------------------------------------

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      className="btn btn-ghost btn-sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
          setDone(true)
          setTimeout(() => setDone(false), 1400)
        } catch {
          // Clipboard is permission-gated and can simply refuse; nothing to fix.
        }
      }}
    >
      {done ? 'Copied' : label}
    </button>
  )
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 45) return 'just now'
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`
  if (secs < 2592000) return `${Math.round(secs / 86400)}d ago`
  return new Date(iso).toLocaleDateString()
}

/** The future-tense counterpart: "in 3m", "due now". */
export function untilTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const secs = Math.round((then - Date.now()) / 1000)
  if (secs <= 5) return 'due now'
  if (secs < 60) return `in ${secs}s`
  if (secs < 3600) return `in ${Math.round(secs / 60)}m`
  if (secs < 86400) return `in ${Math.round(secs / 3600)}h`
  return `in ${Math.round(secs / 86400)}d`
}

/**
 * Re-renders the caller every `ms`, so relative times ("3m ago", "in 40s")
 * keep moving between data refreshes.
 */
export function useNow(ms = 15_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms)
    return () => window.clearInterval(id)
  }, [ms])
  return now
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)}s`
}
